import MojitoCore from 'mojito/core';

import {resolveBackground, setAlpha} from '#core/utils/color-utils.js';
import {isMobile} from '#core/utils/context-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {registerTileSchema} from '#core/application/schema-registry.js';

import {generateSecondaryDropdown} from '#core/application/modules/tiles/dropdown/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generateParticipants} from '#core/application/modules/tiles/participants/index.js';
import {generateMiniScoreboard} from '#core/application/modules/tiles/mini-scoreboard/index.js';
import {generateEarlyPayoutIndicator} from '#core/application/modules/tiles/bonus-indicator/index.js';
import {generateFavoriteIcon} from '#core/application/modules/tiles/favorite-icon/index.js';

import EVENT_LIST from './event-list.palette-schema.yaml';
import EVENT_DETAILS from './event-details.palette-schema.yaml';
import EVENT_LIST_HEADER from './event-list-header.palette-schema.yaml';

const merge = MojitoCore.Presentation.uiStyle.merge;

registerTileSchema(EVENT_LIST);
registerTileSchema(EVENT_DETAILS);
registerTileSchema(EVENT_LIST_HEADER);

export function generateEventList(TILE) {
    return {
        header: generateEventListHeader(!isMobile() ? TILE.EVENT_LIST_HEADER : TILE.EVENT_LIST_MOBILE_HEADER),
        // moved it to separate function just for convenience
        container: {
            itemSpacing: 0, // override mojito defaults
            separator: {
                style: {
                    size: 0,
                },
            },
            style: {
                ...resolveBackground(TILE.EVENT_LIST_BG),
            },
        },
        eventGroupsContainer: {
            itemSpacing: 0,
            separator: {
                style: {
                    color: TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR,
                    size:
                        TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR &&
                        TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR !== 'transparent' &&
                        TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR !== 'none' &&
                        TILE.EVENT_LIST_GROUP_EVENTS_SEPARATOR_COLOR !== 'transparent'
                            ? 1
                            : 0,
                },
            },
        },
        eventListGroup: generateEventListGroup(TILE),
        eventItem: generateEventListItem(TILE),
        eventItemsContainer: {
            style: {
                ...resolveBackground(TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR),
            },
        },
        navigationBarButton: {
            button: {
                button: {
                    style: {
                        base: {
                            ...resolveBackground(TILE.EVENT_LIST_ALL_EVENTS_CONTAINER_BG),
                            height: '40px',
                            borderStyle: 'solid',
                            borderWidth: '1px 0 0 0',
                            borderColor: TILE.EVENT_LIST_ALL_EVENTS_CONTAINER_SEPARATOR_COLOR,
                        },
                    },
                },
                buttonLabel: {
                    style: {
                        ...TILE.EVENT_LIST_ALL_EVENTS_LINK.SECONDARY_LINK_TEXT,
                    },
                },
                buttonIcon: icon(TILE.EVENT_LIST_ALL_EVENTS_LINK.SECONDARY_LINK_ICON_COLOR),
            },
        },
    };
}

function generateEventListHeader(TILE) {
    const EVENT_LIST_HEADER_MARKETS_DROPDOWN = merge(
        generateSecondaryDropdown(TILE.EVENT_LIST_HEADER_MARKETS_DROPDOWN),
        {
            style: {
                rootContainer: {
                    height: isMobile() ? 40 : 32,
                },
            },
            controlButton: {
                style: {
                    base: {
                        flexGrow: 1,
                    },
                },
            },
        }
    );

    return {
        container: {
            itemSpacing: 8,
            style: {
                minHeight: '49px',
                _mobile_minHeight: '40px',
                _mobile_padding: '0 8px',
                _mobile_alignItems: 'center',
                ...resolveBackground(TILE.EVENT_LIST_HEADER_BG),
                ...(TILE.EVENT_LIST_HEADER_WITH_SEPARATOR
                    ? {
                          borderWidth: '0 0 1px 0',
                          borderStyle: 'solid',
                          borderColor: TILE.EVENT_LIST_HEADER_SEPARATOR_COLOR,
                      }
                    : {
                          borderWidth: 0,
                      }),
            },
        },
        title: {
            container: {
                padding: '8px 0',
            },
            style: {
                ...TILE.EVENT_LIST_HEADER_TEXT,
            },
        },
        sortingIcon: icon(TILE.EVENT_LIST_HEADER_SORT_ICON_COLOR),
        sortEventsText: {
            style: {
                ...TILE.EVENT_LIST_HEADER_SORT_TEXT,
            },
        },
        marketOptionsContainer: {
            container: {
                style: {
                    _mobile_width: 'initial',
                    _mobile_minWidth: '100px',
                },
            },
        },
        gameLineSwitcher: EVENT_LIST_HEADER_MARKETS_DROPDOWN,
        marketSwitcher: EVENT_LIST_HEADER_MARKETS_DROPDOWN,
        marketHeader: {
            style: {
                ...TILE.EVENT_LIST_HEADER_MARKETS_TEXT,
            },
        },
    };
}

function generateEventListGroup(TILE) {
    return {
        container: {
            style: {
                ...resolveBackground(TILE.EVENT_LIST_GROUP_BG),
                padding: 0,
            },
        },
        headerButton: {
            style: {
                base: {
                    ...resolveBackground(TILE.EVENT_LIST_GROUP_HEADER_BG),
                    height: 40,
                    _mobile_height: 50,
                    _mobile_padding: '0px 8px',
                    padding: '0px 8px',
                    alignItems: 'center',
                },
            },
        },
        headerSegmentsContainer: {
            sportWidthOverride: {
                _mobile_tennis: '124px',
                _mobile_volleyball: '124px',
                _mobile_beach_volleyball: '124px',
                _mobile_badminton: '124px',
                _mobile_table_tennis: '124px',
                _mobile_squash: '124px',
                _mobile_padel: '124px',
                tennis: '62%',
                volleyball: '62%',
                beach_volleyball: '62%',
                badminton: '62%',
                table_tennis: '62%',
                squash: '62%',
                padel: '62%',
            },
        },
        rotation: {
            image: {
                style: {
                    minWidth: '8px',
                    minHeight: '8px',
                    fill: TILE.EVENT_LIST_GROUP_HEADER_EXPAND_ICON_COLOR,
                },
            },
        },
        headerPrimaryLabel: {
            style: {
                ...TILE.EVENT_LIST_GROUP_NAME_TEXT,
            },
        },
        headerSecondaryLabel: {
            style: {
                ...TILE.EVENT_LIST_GROUP_CLASS_TEXT,
            },
        },
        headerSegmentLabel: {
            style: {
                ...TILE.EVENT_LIST_GROUP_MARKET_TEXT,
            },
            truncate: true,
        },
        earlyPayoutOffers: generateEventListOffers(TILE),
    };
}

export function generateEventListItem(TILE) {
    const EVENT_LIST_ITEM_FAVORITE_ICON = generateFavoriteIcon(TILE.EVENT_LIST_ITEM_FAVORITE_ICON);
    const EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT = {
        textAlign: 'center',
        lineHeight: 1,
        height: '100%',
        ...TILE.EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT,
    };

    return {
        container: {
            style: {
                ...resolveBackground(TILE.EVENT_LIST_ITEM_BG),
            },
        },
        longPressTapableContainerActive: {
            style: {
                base: {
                    backgroundColor: setAlpha(TILE.EVENT_LIST_ITEM_BG, 0.5),
                },
            },
        },
        favoriteIcon: {
            buttonIcon: EVENT_LIST_ITEM_FAVORITE_ICON.favoriteIcon,
        },
        favoriteIconToggled: {
            buttonIcon: EVENT_LIST_ITEM_FAVORITE_ICON.favoriteIconToggled,
        },
        eventDetailsRow: generateEventTimingStatus(TILE.EVENT_LIST_ITEM_TIMING_STATUS), // eventDetailsRow 🥴
        fallbackMarketNameContainer: {
            container: {
                style: {
                    top: '-20px',
                    height: '20px',
                    alignItems: 'flex-end',
                    padding: 0,
                },
            },
        },
        fallbackMarketName: {
            style: EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT,
        },
        fallbackMarketNameEndingInfo: {
            style: EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT,
        },
        marketCountButton: {
            button: {
                style: {
                    base: {
                        width: 'fit-content',
                        ...TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_TEXT,
                    },
                },
            },
            buttonIcon: {
                style: {
                    fill: TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_ICON_COLOR,
                },
            },
            buttonLabel: {
                truncate: true,
                container: {
                    maxWidth: '125px',
                },
            },
        },
        moreMarketsButton: {
            button: {
                style: {
                    base: {
                        width: 'fit-content',
                        ...TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_TEXT,
                    },
                },
                stopClickEventPropagation: false, // todo remove after MOJ-18461
            },
            buttonIcon: {
                style: {
                    fill: TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_ICON_COLOR,
                },
            },
            buttonLabel: {
                truncate: true,
            },
        },
        marketsSectionContainer: {
            container: {
                style: {
                    minHeight: 40,
                },
            },
            sportWidthOverride: {
                _mobile_tennis: '124px',
                _mobile_volleyball: '124px',
                _mobile_beach_volleyball: '124px',
                _mobile_badminton: '124px',
                _mobile_table_tennis: '124px',
                _mobile_squash: '124px',
                _mobile_padel: '124px',
                tennis: '62%',
                volleyball: '62%',
                beach_volleyball: '62%',
                badminton: '62%',
                table_tennis: '62%',
                squash: '62%',
                padel: '62%',
            },
        },
        itemDetails: merge(generateEventListItemDetails(TILE.EVENT_LIST_COMMON_ITEM_DETAILS), {
            container: {
                style: {
                    padding: '0 8px 0 0',
                    _mobile_width: '100%',
                },
            },
            _mobile_participantsAndScoreContainer: {
                style: {
                    justifyContent: 'space-between',
                },
            },
        }),
        itemDetailsGameLine: merge(generateEventListItemDetails(TILE.EVENT_LIST_GAME_LINE_ITEM_DETAILS), {
            participants: {
                participantItem: {
                    text: {
                        ellipsisOnLineNumber: 1,
                    },
                },
            },
        }),
        eventNameLabel: {
            style: {
                ...TILE.EVENT_LIST_ITEM_OUTRIGHT_EVENT_NAME,
            },
        },
        genericMarketContent: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                },
            },
        },
        gameLineMarketContent: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                },
            },
        },
    };
}

export function generateEventListItemDetails(TILE) {
    return {
        container: {
            style: {
                ...resolveBackground(TILE.EVENT_DETAILS_BG),
            },
        },
        participants: merge(generateParticipants(TILE.EVENT_DETAILS_PARTICIPANTS), {
            container: {
                style: {
                    justifyContent: 'space-around',
                },
            },
            participantItem: {
                startingPitcherName: {
                    container: {
                        flexGrow: 0,
                    },
                },
            },
        }),
        scoreboard: merge(generateMiniScoreboard(TILE.EVENT_DETAILS_MINI_SCOREBOARD), {
            segmentIndicators: {
                text: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                },
            },
        }),
    };
}

export function generateEventListOffers(TILE) {
    return {
        offersContainer: {
            style: {
                padding: '8px 0',
            },
        },
        offersText: {
            style: {
                ...TILE.EVENT_LIST_GROUP_OFFERS_LABEL_TEXT,
            },
        },
        earlyPayoutIndicator: generateEarlyPayoutIndicator(TILE.EVENT_LIST_GROUP_EARLY_PAYOUT_INDICATOR),
    };
}
